<template>
  <b-sidebar
    id="sidebar-create-scientific-work"
    sidebar-class="sidebar-lg"
    bg-variant="white"
    shadow
    backdrop
    no-header
    right
  >
    <template #default="{ hide }">
      <!-- Header -->
      <div class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1">
        <h5 class="mb-0">
          {{ scientificWork.id ? $t('Update') : $t('Create') }}
        </h5>
        <feather-icon
          class="ml-1 cursor-pointer"
          icon="XIcon"
          size="16"
          @click="clear(hide)"
        />

      </div>

      <!-- Body -->
      <b-form
        class="p-2"
        @submit.prevent
      >
        <validation-observer ref="scientificWorkCreate">

          <!-- Scientific work type -->
          <b-form-group
            :label="$t('Scientific work type')"
            label-for="scientific_work_type"
          >
            <validation-provider
              #default="{ errors }"
              :name="$t('Scientific work type')"
              rules="required"
            >
              <v-select
                id="dc_type"
                v-model="scientificWork.scientific_work_type"
                :reduce="(option) => option.id"
                :options="scientificWorkTypes"
                :get-option-label="(option) => $langName(option)"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <!-- Scientific work name -->
          <b-form-group
            :label="$t('Scientific work name')"
            label-for="scientific_work_name"
          >
            <validation-provider
              #default="{ errors }"
              :name="$t('Scientific work name')"
              rules="required"
            >
              <b-form-input
                id="scientific_work_name"
                v-model="scientificWork.scientific_work_name"
                :state="errors.length > 0 ? false:null"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <!-- Publication date -->
          <b-form-group
            :label="$t('Publication date')"
            label-for="publication_date"
          >
            <validation-provider
              #default="{ errors }"
              :name="$t('Publication date')"
              rules="required"
            >
              <b-form-datepicker
                id="publication_date"
                v-model="scientificWork.publication_date"
                :state="errors.length > 0 ? false:null"
                :locale="$i18n.locale"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <!-- Scientific work url -->
          <b-form-group
            :label="$t('Scientific work url')"
            label-for="scientific_work_url"
          >
            <validation-provider
              #default="{ errors }"
              :name="$t('Scientific work url')"
              rules="url"
            >
              <b-form-input
                id="scientific_work_url"
                v-model="scientificWork.scientific_work_url"
                :state="errors.length > 0 ? false:null"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <b-form-group
            :label="$t('File')"
            label-for="scientific_work"
          >
            <div
              v-if="scientificWork.scientific_work"
              class="input-group"
            >
              <div class="input-group-prepend">
                <span class="input-group-text border-success">
                  <b-link
                    :href="scientificWork.scientific_work"
                    download="file"
                    target="_blank"
                  >
                    <feather-icon
                      icon="DownloadIcon"
                      class="cursor-pointer"
                      size="16"
                    />
                  </b-link>

                </span>
              </div>
              <div
                class="form-control border-success"
              >
                {{ $t('File') }}
              </div>
              <div
                class="input-group-append"
              >
                <span class="input-group-text">
                  <feather-icon
                    icon="XIcon"
                    class="cursor-pointer danger"
                    size="16"
                    @click="cancel"
                  />
                </span>
              </div>
            </div>
            <validation-provider
              v-else
              #default="{ errors }"
              :name="$t('File')"
              rules="required|file-size:10"
            >
              <b-form-file
                v-model="scientificWork.file"
                :placeholder="$t('Choose a file or drop it here...')"
                accept="application/pdf"
                :drop-placeholder="$t('Drop file here...')"
                :state="errors.length > 0 ? false:null"
                @change="uploadScientificWork"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>
          <!-- Form Actions -->
          <div class="d-flex mt-2">
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="primary"
              class="mr-2"
              type="submit"
              @click="submit(hide)"
            >
              {{ scientificWork.id ? $t('Update') : $t('Create') }}
            </b-button>
            <b-button
              v-ripple.400="'rgba(186, 191, 199, 0.15)'"
              variant="outline-secondary"
              @click="clear(hide)"
            >
              {{ $t('Cancel') }}
            </b-button>
          </div>
        </validation-observer></b-form>
    </template>
  </b-sidebar>
</template>

<script>
import {
  BSidebar, BForm, BFormGroup, BButton, BFormInput, BFormDatepicker, BFormFile, BLink,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import vSelect from 'vue-select'
import { ValidationProvider, ValidationObserver, localize } from 'vee-validate'
import { required, url } from '@/libs/validations/validations'

export default {
  components: {
    ValidationProvider,
    ValidationObserver,
    BSidebar,
    BForm,
    BFormGroup,
    BButton,
    vSelect,
    BFormInput,
    BFormDatepicker,
    BFormFile,
    BLink,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      required,
      url,
    }
  },
  computed: {
    scientificWork: {
      get() {
        return this.$store.state.scientificWork.scientificWork
      },
      set(data) {
        this.$store.commit('scientificWork/UPDATE', data)
      },
    },
    scientificWorkTypes() {
      return this.$store.state.staticModule.scientificWorkTypes
    },
    applyDocument() {
      return this.$store.state.applyDocument.applyDocument
    },
  },
  created() {
    localize(this.$i18n.locale)
    this.$store.dispatch('staticModule/scientificWorkTypes')
  },
  methods: {
    cancel() {
      this.scientificWork.scientific_work = null
      this.scientificWork.file = null
    },
    uploadScientificWork(e) {
      // eslint-disable-next-line prefer-destructuring
      this.file = e.target.files[0]
    },
    clear(hide) {
      this.$store.commit('scientificWork/FRESH')
      hide()
    },
    submit(hide) {
      this.scientificWork.applyDocument = this.applyDocument
      this.$refs.scientificWorkCreate.validate().then(success => {
        if (success) {
          this.$createSwal(this, this.scientificWork, 'scientificWork/create', () => {
            this.$emit('refetchData')
            this.$store.commit('scientificWork/FRESH')
          })
          hide()
        }
      })
    },
  },
}
</script>
